import React from 'react'
import {
	Card,
	Col,
	Row,
	Input,
	Button,
	Typography,
	Form as AntForm,
	message,
} from 'antd'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import {
	useGetFrontendVersionQuery,
	useUpdateFrontendVersionMutation,
} from 'modules/appConfig/requests/queries'
import {
	useGetIsLogsEnabledQuery,
	useSetIsLogsEnabledMutation,
} from 'modules/logRocket/requests'
import { Switch } from 'modules/common/components'

const { Title } = Typography

const schema = yup.object({
	version: yup
		.string()
		.required('Version is required')
		.matches(/^(\d+\.)?(\d+\.)?(\*|\d+)$/, 'Invalid version format'),
})

type FormData = yup.InferType<typeof schema>

export const Configurations: React.FC = () => {
	const { t } = useTranslation('common', { keyPrefix: 'Configurations' })

	const { data: versionConfig, refetch } = useGetFrontendVersionQuery()

	const [updateFrontendVersion, { isLoading: isUpdatingVersion }] =
		useUpdateFrontendVersionMutation()

	const [setIsLogsEnabled, setIsLogsEnabledMutation] =
		useSetIsLogsEnabledMutation()
	const isLoggingEnabledQuery = useGetIsLogsEnabledQuery()
	const isLogsEnabled = isLoggingEnabledQuery.data?.isEnabled

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues: {
			version: versionConfig?.frontendVersion || '',
		},
	})

	const onSubmit = async (values: FormData) => {
		try {
			await updateFrontendVersion({ version: values.version }).unwrap()
			message.success(t('versionUpdated', 'Version updated successfully'))
			refetch()
			reset({ version: values.version })
		} catch (e) {
			console.error(e)
			message.error(t('versionUpdateFailed', 'Failed to update version'))
		}
	}

	const onSwitchLogs = async (isEnabled: boolean) => {
		await setIsLogsEnabled(isEnabled)
		isLoggingEnabledQuery.refetch()
	}

	return (
		<Card>
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<Title level={2}>{t('configurations')}</Title>
				</Col>

				<Col span={24}>
					{t('logRocketLogs')}:&nbsp;
					<Switch
						loading={
							isLoggingEnabledQuery.isFetching ||
							setIsLogsEnabledMutation.isLoading
						}
						value={isLogsEnabled}
						onClick={onSwitchLogs}
					/>
				</Col>

				<Col span={24}>
					<Title level={4}>
						{t('frontendVersion', { value: versionConfig?.frontendVersion })}
					</Title>
					<p></p>

					<AntForm layout='vertical'>
						<AntForm.Item
							label={t('newVersion', 'New Version')}
							validateStatus={errors.version ? 'error' : ''}
							help={errors.version?.message}
						>
							<Controller
								name='version'
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										placeholder='e.g., 1.0.0'
										disabled={isUpdatingVersion}
									/>
								)}
							/>
						</AntForm.Item>

						<AntForm.Item>
							<Button
								type='primary'
								onClick={handleSubmit(onSubmit)}
								loading={isSubmitting || isUpdatingVersion}
							>
								{t('update')}
							</Button>
						</AntForm.Item>
					</AntForm>
				</Col>
			</Row>
		</Card>
	)
}
